export const IMPOTS_LESSONS = [
  {
    id: 'impots-1',
    numero: 1,
    titre: "Comprendre la déclaration de revenus",
    sousTitre: "Pourquoi et quand ?",
    contexte: "Chaque année en avril-mai, les Français déclarent leurs revenus. Mais pourquoi exactement, et que se passe-t-il si on ne le fait pas ?",
    explication: [
      "**La déclaration de revenus** permet à l'État de calculer l'impôt que vous devez payer — ou le remboursement que vous pouvez recevoir.",
      "**Qui doit déclarer ?** Toute personne majeure résidant en France, même si vous n'avez pas de revenus ou si vous êtes exonéré d'impôts.",
      "**Quand déclarer ?** Chaque année entre avril et juin. La date limite varie selon votre département — vérifiez sur impots.gouv.fr.",
      "**Si vous ne déclarez pas**, vous risquez une pénalité de 10% sur votre impôt, puis des majorations supplémentaires. En cas de doute, déclarez toujours.",
      "**Bonne nouvelle** : si vous avez déjà déclaré l'an dernier, vos informations sont pré-remplies. Il suffit souvent de vérifier et valider.",
    ],
    question: "Qui doit faire une déclaration de revenus en France ?",
    options: [
      { id: 'a', text: "Toute personne majeure résidant en France, même sans revenus", correct: true },
      { id: 'b', text: "Seulement les personnes qui paient des impôts", correct: false },
      { id: 'c', text: "Seulement les salariés en CDI", correct: false },
    ],
    explication_reponse: "Tout le monde doit déclarer, même sans revenus — cela permet à l'État de calculer vos droits à certaines aides. Ne pas déclarer peut entraîner des pénalités, même si vous n'êtes pas imposable.",
  },
  {
    id: 'impots-2',
    numero: 2,
    titre: "Créer son espace personnel impots.gouv.fr",
    sousTitre: "La première connexion",
    contexte: "Tout se passe en ligne sur impots.gouv.fr. La première étape est de créer ou retrouver votre espace personnel.",
    explication: [
      "**Le site officiel** est impots.gouv.fr — uniquement ce site. Méfiez-vous des sites qui ressemblent mais ont une adresse différente.",
      "**Première connexion** : cliquez sur 'Votre espace particulier' → 'Créer mon espace'. Il vous faudra votre numéro fiscal (sur votre ancienne déclaration ou avis d'imposition).",
      "**Votre numéro fiscal** est un numéro à 13 chiffres qui commence par 0, 1, 2 ou 3. Il est inscrit en haut à gauche de votre avis d'imposition.",
      "**Si c'est votre première déclaration**, appelez le 0809 401 401 (numéro gratuit) — un agent vous guidera pour créer votre accès.",
      "**Mot de passe** : choisissez-en un solide (12 caractères minimum) et notez-le dans un endroit sûr. Ne le communiquez jamais.",
    ],
    question: "Où se trouve votre numéro fiscal sur un avis d'imposition ?",
    options: [
      { id: 'a', text: "En haut à gauche de l'avis d'imposition", correct: true },
      { id: 'b', text: "En bas de page après la signature", correct: false },
      { id: 'c', text: "Il faut l'appeler pour l'obtenir", correct: false },
    ],
    explication_reponse: "Le numéro fiscal (13 chiffres) est toujours en haut à gauche de votre avis d'imposition ou déclaration pré-remplie. Conservez précieusement ce document — il vous sera demandé chaque année.",
  },
  {
    id: 'impots-3',
    numero: 3,
    titre: "Remplir sa déclaration en ligne",
    sousTitre: "Étape par étape",
    contexte: "Vous êtes connecté sur impots.gouv.fr. La déclaration pré-remplie vous attend. Comment la compléter correctement ?",
    explication: [
      "**La déclaration pré-remplie** contient déjà vos salaires, pensions et revenus connus de l'administration. Vérifiez qu'ils sont corrects.",
      "**Vérifiez surtout** : votre situation familiale (marié, pacsé, enfants à charge), vos revenus si vous avez changé de situation, vos dépenses déductibles.",
      "**Les cases importantes** : case 1AJ (salaires), case 2BH (revenus de placement), case 7DB (dons aux associations). Ne remplissez que ce qui vous concerne.",
      "**En cas de doute** sur une case : laissez-la vide plutôt que de mettre un chiffre faux. Vous pouvez corriger jusqu'à la date limite.",
      "**Avant de valider** : relisez le récapitulatif. Un montant d'impôt s'affiche — s'il vous semble anormal, vérifiez avant de soumettre.",
    ],
    question: "Votre déclaration pré-remplie indique un salaire incorrect. Que faire ?",
    options: [
      { id: 'a', text: "Corriger directement le montant avec votre bulletin de salaire comme référence", correct: true },
      { id: 'b', text: "Valider quand même — l'administration corrigera automatiquement", correct: false },
      { id: 'c', text: "Ne pas déclarer cette année et attendre", correct: false },
    ],
    explication_reponse: "Vous pouvez et devez corriger les montants erronés. Munissez-vous de vos bulletins de salaire ou relevés et saisissez le bon montant. L'administration ne corrige pas automatiquement — c'est votre responsabilité de déclarer les bons chiffres.",
  },
  {
    id: 'impots-4',
    numero: 4,
    titre: "Comprendre son avis d'imposition",
    sousTitre: "Lire le résultat",
    contexte: "Après votre déclaration, vous recevez un avis d'imposition en juillet-août. Comment le lire et que faire ?",
    explication: [
      "**L'avis d'imposition** arrive en juillet-août sur votre espace en ligne (et par courrier si vous l'avez demandé). C'est le document officiel qui confirme votre impôt.",
      "**Les informations clés** : revenu fiscal de référence (utile pour beaucoup d'aides sociales), montant de l'impôt dû, reste à payer ou remboursement.",
      "**Le prélèvement à la source** : depuis 2019, l'impôt est prélevé directement sur votre salaire chaque mois. L'avis régularise la différence.",
      "**Si vous avez trop payé** : un remboursement est effectué automatiquement sur votre compte bancaire en août-septembre.",
      "**Conservez cet avis** précieusement — il vous sera demandé pour de nombreuses démarches : logement social, aides CAF, prêt bancaire...",
    ],
    question: "À quoi sert le revenu fiscal de référence indiqué sur votre avis d'imposition ?",
    options: [
      { id: 'a', text: "À calculer vos droits à de nombreuses aides sociales et démarches", correct: true },
      { id: 'b', text: "Uniquement à calculer votre impôt", correct: false },
      { id: 'c', text: "C'est un chiffre informatif sans utilité pratique", correct: false },
    ],
    explication_reponse: "Le revenu fiscal de référence est un chiffre clé utilisé partout : tarifs de cantine scolaire, aides au logement, complémentaire santé solidaire, tarifs hospitaliers... Conservez toujours votre avis d'imposition à portée de main.",
  },
];
